import React, { useEffect, useState, useContext } from "react";
import secureAxios from "../../utils/secureAxios";
import { getApiBaseURL } from "../../utils/api";
import { AuthContext } from "../../contexts/AuthContext";

const TenantList = ({ onEditTenant, refreshTrigger }) => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiBase = getApiBaseURL();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    fetchTenants();
  }, [refreshTrigger]);

  const fetchTenants = async () => {
    setLoading(true);
    setError(null);

    try {
      // Using secureAxios for HIPAA-compliant authenticated requests
      const response = await secureAxios.get(`${apiBase}/api/tenants`);
      setTenants(response.data);
    } catch (err) {
      console.error("❌ Error loading tenants:", err);
      if (err.response?.status === 403) {
        setError("Access denied. Primary tenant access required.");
      } else {
        setError("Failed to load tenants. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading tenants...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
        <button
          className="btn btn-sm btn-outline-danger ms-3"
          onClick={fetchTenants}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>All Tenants</h4>
        <span className="text-muted">Total: {tenants.length} tenants</span>
      </div>

      {/* Desktop Table View */}
      <div className="table-responsive d-none d-md-block">
        <table className="table table-hover">
          <thead className="table-light">
            <tr>
              <th>Tenant Name</th>
              <th>Type</th>
              <th>Phone</th>
              <th>City</th>
              <th>Status</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tenants.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center text-muted">
                  No tenants found.
                </td>
              </tr>
            ) : (
              tenants.map((tenant) => {
                return (
                  <tr key={tenant.tenant_id}>
                    <td>
                      <strong>{tenant.tenant_name}</strong>
                      {tenant.is_primary && (
                        <span className="badge bg-primary ms-2">Primary</span>
                      )}
                    </td>
                    <td>
                      <span className="badge bg-secondary">
                        {tenant.tenant_type || "Customer"}
                      </span>
                    </td>
                    <td>{tenant.phone_number || "—"}</td>
                    <td>
                      {tenant.address_line_1 ? (
                        <small>
                          {tenant.city || "—"}
                          {tenant.state_id && ", "}
                          {tenant.postal_code || ""}
                        </small>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          tenant.is_active ? "bg-success" : "bg-danger"
                        }`}
                      >
                        {tenant.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="text-end">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => onEditTenant(tenant)}
                        title="Edit tenant"
                      >
                        <i className="bi bi-pencil"></i> Edit
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="d-block d-md-none">
        {tenants.length === 0 ? (
          <div className="alert alert-info">No tenants found.</div>
        ) : (
          tenants.map((tenant) => (
            <div key={tenant.tenant_id} className="card mb-3">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h5 className="card-title">
                      {tenant.tenant_name}
                      {tenant.is_primary && (
                        <span className="badge bg-primary ms-2">Primary</span>
                      )}
                    </h5>
                    <p className="card-text">
                      <span className="badge bg-secondary me-2">
                        {tenant.tenant_type || "Customer"}
                      </span>
                      <span
                        className={`badge ${
                          tenant.is_active ? "bg-success" : "bg-danger"
                        }`}
                      >
                        {tenant.is_active ? "Active" : "Inactive"}
                      </span>
                    </p>
                    {tenant.phone_number && (
                      <p className="mb-1">
                        <i className="bi bi-telephone"></i>{" "}
                        {tenant.phone_number}
                      </p>
                    )}
                    {tenant.city && (
                      <p className="mb-0">
                        <i className="bi bi-geo-alt"></i> {tenant.city}
                      </p>
                    )}
                  </div>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => onEditTenant(tenant)}
                  >
                    Edit
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* PHI Notice */}
      <div className="mt-3">
        <small className="text-muted">
          <i className="bi bi-shield-lock"></i> This list contains Protected
          Health Information (PHI). All access is logged for HIPAA compliance.
        </small>
      </div>
    </div>
  );
};

export default TenantList;
